import Box from "@mui/material/Box";

import type { JSX, ReactNode } from "react";

export function pickHighlight(
  highlights: Record<string, string[]> | undefined,
  ...keys: string[]
): string | undefined {
  if (!highlights) {
    return undefined;
  }
  for (const key of keys) {
    const frags = highlights[key];
    if (frags && frags.length > 0) {
      return frags[0];
    }
  }
  return undefined;
}

export function renderHighlight(
  fallback: string,
  fragment?: string
): ReactNode {
  if (!fragment) {
    return fallback;
  }

  const parts = fragment
    .split(/<em>(.*?)<\/em>/)
    .map((part, index) =>
      index % 2 === 1 ? <mark key={part}>{part}</mark> : part
    );

  if (parts.length === 0) {
    return fallback;
  }
  return parts.length === 1 ? parts[0] : parts;
}

function wrapText(
  currentArray: (string | JSX.Element)[],
  allMatches: string[]
): JSX.Element | null {
  if (!allMatches?.length) {
    return <span>{currentArray}</span>;
  }
  const [currentMatch, ...remainingMatches] = allMatches;
  const wrappedTextArray = currentArray.flatMap((el) => {
    if (typeof el !== "string" || !currentMatch) {
      return el;
    }
    if (!el.includes(currentMatch)) {
      return el;
    }
    return el.split(currentMatch).flatMap((substring, index, { length }) => {
      if (index + 1 === length) {
        return substring;
      }
      return [
        substring,
        <Box
          key={`${
            // biome-ignore lint/suspicious/noArrayIndexKey: no better unique key available
            index
          }-${currentMatch}`}
          component="span"
          sx={{
            backgroundColor: "lightblue",
            borderRadius: "2px",
          }}
        >
          {currentMatch}
        </Box>,
      ];
    });
  });
  return wrapText(wrappedTextArray, remainingMatches);
}

export function HighlightText({
  text,
  searchTerm,
}: {
  text: string;
  searchTerm: string;
}): JSX.Element | null {
  if (!text || typeof text !== "string") {
    return null;
  }
  if (!searchTerm || typeof searchTerm !== "string") {
    return <>{text}</>;
  }
  const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(escapedTerm, "gi");
  const matches = text.match(regex);
  if (matches === null) {
    return <>{text}</>;
  }
  return wrapText([text], matches);
}
